import React, { useState, useEffect, useContext, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Card, Button, Steps, Alert, Spin, Result } from "antd";
import {
  CheckCircleOutlined,
  LoadingOutlined,
  ExclamationCircleOutlined,
  BankOutlined,
  CreditCardOutlined,
  SafetyOutlined,
  DollarOutlined,
  SettingOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { AuthContext } from "../context/AuthProvider";
import {
  createConnectAccount,
  createAccountLink,
  getConnectAccountStatus,
} from "../lib/payments/stripe";
import { updateConnectAccountInSettings } from "../lib/payments/connectStatus";
import { trackEvent } from "../helpers/mixpanel";
import ConfettiBurst from "../components/ConfettiBurst";
import SettingsModel from "../lib/firebase/settingsModel";

const { Step } = Steps;

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 20px;
  background: linear-gradient(
    120deg,
    #181818 0%,
    #2c5f2d 40%,
    #4a7c59 75%,
    #e6f3e6 100%
  );

  .ant-card-body {
    border-radius: 10px;
  }

  .payment-setup-steps {
    .ant-steps-item-title {
      font-family:
        "Inter",
        -apple-system,
        BlinkMacSystemFont,
        "Segoe UI",
        sans-serif;
      font-weight: 600;
      color: #2c5f2d;
      font-size: 16px;
    }

    .ant-steps-item-description {
      font-family:
        "Inter",
        -apple-system,
        BlinkMacSystemFont,
        "Segoe UI",
        sans-serif;
      color: #4a7c59;
      font-size: 14px;
      font-weight: 400;
    }

    .ant-steps-item-process .ant-steps-item-icon {
      background-color: #2c5f2d !important;
      border-color: #2c5f2d !important;
      color: white !important;

      .anticon {
        color: white !important;
      }
    }

    .ant-steps-item-process .ant-steps-item-title {
      color: #2c5f2d !important;
      font-weight: 700;
    }

    .ant-steps-item-process .ant-steps-item-description {
      color: #4a7c59 !important;
      font-weight: 500;
    }

    .ant-steps-item-finish .ant-steps-item-icon {
      background-color: #2c5f2d !important;
      border-color: #2c5f2d !important;
      color: white !important;

      .anticon {
        color: white !important;
      }
    }

    .ant-steps-item-finish .ant-steps-item-title {
      color: #2c5f2d !important;
      font-weight: 600;
    }

    .ant-steps-item-finish .ant-steps-item-description {
      color: #4a7c59 !important;
      font-weight: 500;
    }

    .ant-steps-item-wait .ant-steps-item-icon {
      background-color: #f5f5f5 !important;
      border-color: #d9d9d9 !important;
      color: #999 !important;

      .anticon {
        color: #999 !important;
      }
    }

    .ant-steps-item-wait .ant-steps-item-title {
      color: #999 !important;
      font-weight: 500;
    }

    .ant-steps-item-wait .ant-steps-item-description {
      color: #bbb !important;
      font-weight: 400;
    }
  }

  @media (max-width: 768px) {
    padding: 10px;
    align-items: flex-start;
    padding-top: 40px;
  }
`;

const SetupCard = styled(Card)`
  max-width: 900px;
  width: 100%;
  border-radius: 24px;
  box-shadow:
    0 20px 60px rgba(44, 95, 45, 0.15),
    0 8px 32px rgba(24, 24, 24, 0.08);
  border: 3px solid #2c5f2d;
  background: linear-gradient(135deg, #ffffff 0%, #f8fffe 100%);
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #2c5f2d 0%, #4a7c59 50%, #2c5f2d 100%);
  }

  .ant-card-body {
    padding: 48px 40px;
    border-radius: 20px;
  }

  @media (max-width: 1024px) {
    max-width: 800px;

    .ant-card-body {
      padding: 40px 32px;
    }
  }

  @media (max-width: 768px) {
    border-radius: 20px;
    margin: 0;
    max-width: 98vw;
    border-width: 2px;

    .ant-card-body {
      padding: 32px 24px;
    }
  }
`;

const SuccessContainer = styled.div`
  text-align: center;
  padding: 40px 40px;

  @media (max-width: 1024px) {
    padding: 40px 30px;
  }

  @media (max-width: 768px) {
    padding: 20px 10px;
  }
`;

const SuccessIcon = styled.div`
  font-size: 80px;
  color: #2c5f2d;
  margin-bottom: 24px;
  animation: bounce 2s infinite;

  .anticon {
    font-size: 80px !important;
    line-height: 1;
  }

  @keyframes bounce {
    0%,
    20%,
    50%,
    80%,
    100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(-10px);
    }
    60% {
      transform: translateY(-5px);
    }
  }

  @media (max-width: 768px) {
    font-size: 60px;
    margin-bottom: 16px;

    .anticon {
      font-size: 60px !important;
    }
  }
`;

const ErrorIcon = styled.div`
  font-size: 80px;
  color: #ff4d4f;
  margin-bottom: 24px;
  animation: shake 0.5s ease-in-out;

  .anticon {
    font-size: 80px !important;
    line-height: 1;
  }

  @keyframes shake {
    0%,
    100% {
      transform: translateX(0);
    }
    25% {
      transform: translateX(-5px);
    }
    75% {
      transform: translateX(5px);
    }
  }

  @media (max-width: 768px) {
    font-size: 60px;
    margin-bottom: 16px;

    .anticon {
      font-size: 60px !important;
    }
  }
`;

const ErrorTitle = styled.h1`
  font-size: 32px;
  color: #ff4d4f;
  margin-bottom: 16px;
  font-weight: 700;
  font-family:
    "Inter",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;

  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 12px;
  }
`;

const ErrorSubtitle = styled.p`
  font-size: 18px;
  color: #666;
  margin-bottom: 32px;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 24px;
  }
`;

const ErrorCard = styled.div`
  background: linear-gradient(90deg, #fff2f0 0%, #ffe7e6 100%);
  border: 1.5px solid #ff4d4f;
  border-radius: 14px;
  padding: 20px;
  text-align: center;
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease;
  box-shadow: 0 2px 12px rgba(255, 77, 79, 0.08);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(255, 77, 79, 0.2);
  }

  .anticon {
    font-size: 32px;
    color: #ff4d4f;
    margin-bottom: 12px;
  }

  h3 {
    font-size: 16px;
    font-weight: 600;
    color: #ff4d4f;
    margin-bottom: 8px;
    font-family:
      "Inter",
      -apple-system,
      BlinkMacSystemFont,
      "Segoe UI",
      sans-serif;
  }

  p {
    font-size: 14px;
    color: #d4380d;
    margin: 0;
    font-family:
      "Inter",
      -apple-system,
      BlinkMacSystemFont,
      "Segoe UI",
      sans-serif;
  }

  @media (max-width: 768px) {
    padding: 16px;

    .anticon {
      font-size: 24px;
      margin-bottom: 8px;
    }

    h3 {
      font-size: 14px;
      margin-bottom: 6px;
    }

    p {
      font-size: 12px;
    }
  }
`;

const ErrorText = styled.div`
  background: linear-gradient(90deg, #fff2f0 0%, #ffe7e6 100%);
  border: 1.5px solid #ff4d4f;
  border-radius: 14px;
  padding: 20px;
  margin: 24px 0;
  box-shadow: 0 2px 12px rgba(255, 77, 79, 0.08);

  h3 {
    color: #ff4d4f;
    font-size: 18px;
    font-weight: 700;
    margin-bottom: 8px;
    font-family:
      "Inter",
      -apple-system,
      BlinkMacSystemFont,
      "Segoe UI",
      sans-serif;
  }

  p {
    color: #d4380d;
    font-size: 14px;
    margin: 0;
    font-family:
      "Inter",
      -apple-system,
      BlinkMacSystemFont,
      "Segoe UI",
      sans-serif;
  }

  @media (max-width: 768px) {
    padding: 16px;
    margin: 20px 0;

    h3 {
      font-size: 16px;
    }

    p {
      font-size: 13px;
    }
  }
`;

const SuccessTitle = styled.h1`
  font-size: 32px;
  color: #2c5f2d;
  margin-bottom: 16px;
  font-weight: 700;
  font-family:
    "Inter",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;

  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 12px;
  }
`;

const SuccessSubtitle = styled.p`
  font-size: 18px;
  color: #666;
  margin-bottom: 32px;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 24px;
  }
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin: 32px 0;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
    margin: 24px 0;
  }
`;

const BenefitCard = styled.div`
  background: linear-gradient(90deg, #f0f8f0 0%, #e8f5e8 100%);
  border: 1.5px solid #2c5f2d;
  border-radius: 14px;
  padding: 20px;
  text-align: center;
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease;
  box-shadow: 0 2px 12px rgba(44, 95, 45, 0.08);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(44, 95, 45, 0.2);
  }

  .anticon {
    font-size: 32px;
    color: #2c5f2d;
    margin-bottom: 12px;
  }

  h3 {
    font-size: 16px;
    font-weight: 600;
    color: #2c5f2d;
    margin-bottom: 8px;
    font-family:
      "Inter",
      -apple-system,
      BlinkMacSystemFont,
      "Segoe UI",
      sans-serif;
  }

  p {
    font-size: 14px;
    color: #4a7c59;
    margin: 0;
    font-family:
      "Inter",
      -apple-system,
      BlinkMacSystemFont,
      "Segoe UI",
      sans-serif;
  }

  @media (max-width: 768px) {
    padding: 16px;

    .anticon {
      font-size: 24px;
      margin-bottom: 8px;
    }

    h3 {
      font-size: 14px;
      margin-bottom: 6px;
    }

    p {
      font-size: 12px;
    }
  }
`;

const CelebrationText = styled.div`
  background: linear-gradient(90deg, #f0f8f0 0%, #e8f5e8 100%);
  border: 1.5px solid #2c5f2d;
  border-radius: 14px;
  padding: 20px;
  margin: 24px 0;
  box-shadow: 0 2px 12px rgba(44, 95, 45, 0.08);

  h3 {
    color: #2c5f2d;
    font-size: 18px;
    font-weight: 700;
    margin-bottom: 8px;
    font-family:
      "Inter",
      -apple-system,
      BlinkMacSystemFont,
      "Segoe UI",
      sans-serif;
  }

  p {
    color: #4a7c59;
    font-size: 14px;
    margin: 0;
    font-family:
      "Inter",
      -apple-system,
      BlinkMacSystemFont,
      "Segoe UI",
      sans-serif;
  }

  @media (max-width: 768px) {
    padding: 16px;
    margin: 20px 0;

    h3 {
      font-size: 16px;
    }

    p {
      font-size: 13px;
    }
  }
`;

const ActionButton = styled(Button)`
  height: 50px;
  font-size: 18px;
  font-weight: 700;
  border-radius: 12px;
  background: linear-gradient(90deg, #2c5f2d 0%, #4a7c59 100%);
  border: none;
  box-shadow: 0 4px 16px rgba(44, 95, 45, 0.18);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  font-family:
    "Inter",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;

  &:hover {
    transform: translateY(-2px) scale(1.03);
    box-shadow: 0 8px 32px rgba(44, 95, 45, 0.25);
    background: linear-gradient(90deg, #4a7c59 0%, #2c5f2d 100%);
  }

  @media (max-width: 768px) {
    height: 44px;
    font-size: 16px;
    width: 100%;
  }
`;

const SecondaryButton = styled(Button)`
  height: 50px;
  font-size: 18px;
  font-weight: 600;
  border-radius: 12px;
  background: transparent;
  border: none;
  color: #2c5f2d;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  font-family:
    "Inter",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;

  &:hover {
    transform: translateY(-2px) scale(1.02);
    background: transparent;
    border: none;
    box-shadow: none;
    color: #4a7c59;
  }

  &:focus {
    color: #4a7c59;
    background: transparent;
    border: none;
    box-shadow: none;
  }

  @media (max-width: 768px) {
    height: 44px;
    font-size: 16px;
    width: 100%;
  }
`;

const FeatureList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
  margin: 24px 0;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 18px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
    margin: 20px 0;
  }
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  padding: 20px;
  background: linear-gradient(135deg, #f8fffe 0%, #e8f5e8 100%);
  border: 2px solid #2c5f2d;
  border-radius: 16px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 16px rgba(44, 95, 45, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #2c5f2d 0%, #4a7c59 100%);
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(44, 95, 45, 0.2);
    border-color: #4a7c59;

    &::before {
      transform: scaleX(1);
    }
  }

  .anticon {
    margin-right: 16px;
    font-size: 24px;
    color: #2c5f2d;
    transition: color 0.3s ease;
  }

  span {
    color: #2c5f2d;
    font-family:
      "Inter",
      -apple-system,
      BlinkMacSystemFont,
      "Segoe UI",
      sans-serif;
    font-weight: 600;
    font-size: 16px;
    transition: color 0.3s ease;
  }

  &:hover .anticon,
  &:hover span {
    color: #4a7c59;
  }

  @media (max-width: 768px) {
    padding: 18px;

    .anticon {
      font-size: 20px;
      margin-right: 12px;
    }

    span {
      font-size: 15px;
    }
  }
`;

const PaymentSetup = () => {
  const { user } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [accountId, setAccountId] = useState(null);
  const [accountStatus, setAccountStatus] = useState(null);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState("setup"); // 'setup', 'return', 'success', 'cancelled', 'error'
  const [showConfetti, setShowConfetti] = useState(false);

  const success = searchParams.get("success");
  const account = searchParams.get("account");
  const refresh = searchParams.get("refresh");
  const stripeError = searchParams.get("error");
  const error_description = searchParams.get("error_description");
  const state = searchParams.get("state");
  const code = searchParams.get("code");

  const handleStripeReturn = useCallback(async () => {
    try {
      // User clicked back or canceled during Stripe setup - show failure page
      if (refresh) {
        setMode("cancelled");
        trackEvent("stripe_connect_onboarding_cancelled", {
          reason: "user_back_or_cancel",
          refresh: true,
        });
        return;
      }

      // Check for error parameters first (this handles "Back to New Business Sandbox" cases)
      if (stripeError || error_description) {
        console.log("Stripe Connect error detected:", {
          stripeError,
          error_description,
        });
        setMode("cancelled");

        trackEvent("stripe_connect_onboarding_cancelled", {
          account_id: account,
          error: stripeError,
          error_description: error_description,
        });
        return;
      }

      // Handle back/cancel action: success=false means user clicked back or cancelled
      if (success === "false") {
        console.log(
          "Stripe Connect back/cancel action detected: success=false"
        );
        setMode("cancelled");

        trackEvent("stripe_connect_onboarding_cancelled", {
          reason: "back_action",
          success: success,
          account_id: account,
        });
        return;
      }

      if (success === "true") {
        // Success case - only show success if we have an account ID
        if (account) {
          setMode("checking");
          setAccountId(account);

          try {
            const accountData = await getConnectAccountStatus(account);
            setAccountStatus(accountData);

            // Update Firebase settings with Connect account info
            try {
              await updateConnectAccountInSettings(
                user.uid,
                account,
                accountData
              );
              console.log(
                "Firebase settings updated with Connect account info"
              );
            } catch (updateError) {
              console.error("Error updating Firebase settings:", updateError);
              // Don't fail the flow if Firebase update fails
            }

            // Check if account is fully activated
            const isFullyActivated =
              accountData.charges_enabled &&
              accountData.payouts_enabled &&
              accountData.details_submitted;

            if (isFullyActivated) {
              // Account is fully activated - show success page
              console.log("Stripe Connect account fully activated:", {
                charges_enabled: accountData.charges_enabled,
                payouts_enabled: accountData.payouts_enabled,
                details_submitted: accountData.details_submitted,
              });

              setMode("success");

              trackEvent("stripe_connect_onboarding_completed", {
                account_id: account,
                charges_enabled: accountData.charges_enabled,
                payouts_enabled: accountData.payouts_enabled,
                details_submitted: accountData.details_submitted,
              });
            } else {
              // Account needs verification - show pending state
              console.log("Stripe Connect account pending verification:", {
                charges_enabled: accountData.charges_enabled,
                payouts_enabled: accountData.payouts_enabled,
                details_submitted: accountData.details_submitted,
              });

              setMode("pending");

              trackEvent("stripe_connect_onboarding_pending", {
                account_id: account,
                reason: "kyc_verification_required",
                charges_enabled: accountData.charges_enabled,
                payouts_enabled: accountData.payouts_enabled,
                details_submitted: accountData.details_submitted,
              });
            }
          } catch (accountError) {
            console.error("Error checking account status:", accountError);
            // Even if status check fails, save the account ID so we know onboarding was attempted
            // This handles cases where backend API is unavailable or test/production key mismatch
            console.log(
              "Cannot verify account status, but saving account ID for future checks"
            );

            try {
              // Save account ID with minimal status info
              await updateConnectAccountInSettings(user.uid, account, {
                details_submitted: false,
                charges_enabled: false,
                payouts_enabled: false,
              });
              console.log(
                "Account ID saved to Firebase despite status check failure"
              );

              // Show pending state - user completed onboarding but status is unknown
              setMode("pending");

              trackEvent("stripe_connect_onboarding_pending", {
                account_id: account,
                reason: "status_check_failed_but_account_saved",
                error: accountError.message,
              });
            } catch (saveError) {
              console.error("Error saving account ID:", saveError);
              // If we can't even save the account ID, treat as failed
              setMode("cancelled");

              trackEvent("stripe_connect_onboarding_cancelled", {
                account_id: account,
                reason: "account_status_check_failed_and_save_failed",
                error: accountError.message,
                saveError: saveError.message,
              });
            }
          }
        } else {
          // This should not happen anymore due to the check above
          console.warn(
            "Success=true but no account ID provided - this should be handled by back action detection"
          );
          setMode("cancelled");
        }
      } else if (success === "false") {
        setMode("cancelled");

        trackEvent("stripe_connect_onboarding_cancelled", {
          account_id: account,
        });
      } else {
        setMode("error");
        setError("Invalid return URL parameters");
      }
    } catch (err) {
      console.error("Error handling return:", err);
      setMode("error");
      setError(err.message);
    }
  }, [user, success, account, refresh, stripeError, error_description]);

  useEffect(() => {
    // Debug logging
    console.log("PaymentSetup URL parameters:", {
      success,
      account,
      refresh,
      stripeError,
      error_description,
      state,
      code,
      allParams: Object.fromEntries(searchParams.entries()),
    });

    // Check if this is a return from Stripe
    if (success !== null || refresh !== null || stripeError !== null) {
      // Wait for user authentication before handling Stripe return
      if (user) {
        console.log("User is authenticated, handling Stripe return");
        handleStripeReturn();
      } else {
        console.log("User not authenticated yet, waiting...");
        setMode("checking"); // Show loading state while waiting for auth
      }
    } else {
      // Redirect to settings if no Stripe return parameters
      navigate("/settings");
    }
  }, [
    success,
    account,
    refresh,
    stripeError,
    error_description,
    state,
    code,
    navigate,
    user,
    handleStripeReturn,
    searchParams,
  ]);

  const startOnboarding = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check if user is authenticated
      if (!user) {
        throw new Error("User not authenticated. Please log in to continue.");
      }

      if (!user.email) {
        throw new Error(
          "User email not available. Please ensure your account has a valid email address."
        );
      }

      // Check if user already has an existing Stripe Connect account
      let existingAccountId = null;
      try {
        const userSettings = await SettingsModel.getByUserId(user.uid);
        existingAccountId = userSettings?.paymentSetup?.stripeAccountId || null;
      } catch (settingsError) {
        console.warn("Could not check for existing account:", settingsError);
        // Continue with creating new account if check fails
      }

      let accountIdToUse = null;

      if (existingAccountId) {
        // Resume onboarding for existing account
        console.log(
          "Resuming onboarding for existing account:",
          existingAccountId
        );
        accountIdToUse = existingAccountId;
        setAccountId(existingAccountId);

        trackEvent("stripe_connect_onboarding_resumed", {
          user_id: user.uid,
          account_id: existingAccountId,
        });
      } else {
        // Create new Stripe Connect account
        console.log("Creating new Stripe Connect account");
        const accountResponse = await createConnectAccount(
          user.email,
          user.uid,
          "US"
        );
        accountIdToUse = accountResponse.accountId;
        setAccountId(accountResponse.accountId);

        trackEvent("stripe_connect_onboarding_started", {
          user_id: user.uid,
          account_id: accountResponse.accountId,
        });
      }

      // Create account link for onboarding (works for both new and existing accounts)
      const linkResponse = await createAccountLink(
        accountIdToUse,
        `${window.location.origin}/payment-setup?refresh=true`,
        `${window.location.origin}/payment-setup?success=true&account=${accountIdToUse}`
      );

      // Redirect to Stripe onboarding
      window.location.href = linkResponse.url;
    } catch (error) {
      console.error("Onboarding error:", error);

      // Provide more helpful error messages
      let errorMessage = error.message || "Failed to start onboarding process";

      if (errorMessage.includes("User not authenticated")) {
        errorMessage =
          "Please log in to your account to set up payment processing.";
      } else if (errorMessage.includes("User email not available")) {
        errorMessage =
          "Your account needs a valid email address to set up payment processing.";
      } else if (
        errorMessage.includes("Backend API endpoint not available") ||
        errorMessage.includes("Payment processing is not yet configured")
      ) {
        errorMessage =
          "Payment processing backend is not yet configured. Please contact your developer to implement the required API endpoints.";
      } else if (errorMessage.includes("Failed to fetch")) {
        errorMessage =
          "Unable to connect to the payment service. Please check your internet connection and try again.";
      }

      setError(errorMessage);

      trackEvent("stripe_connect_onboarding_failed", {
        user_id: user?.uid,
        error: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = () => {
    navigate("/dashboard");
  };

  const handleRetry = () => {
    navigate("/payment-setup");
  };

  const handleConfettiComplete = () => {
    setShowConfetti(false);
  };

  if (!user) {
    return (
      <Container>
        <SetupCard>
          <Result
            icon={<ExclamationCircleOutlined />}
            title="Authentication Required"
            subTitle="Please log in to set up payment processing."
            extra={
              <Button type="primary" href="/login">
                Go to Login
              </Button>
            }
          />
        </SetupCard>
      </Container>
    );
  }

  const renderContent = () => {
    switch (mode) {
      case "setup":
        return (
          <>
            <Steps
              current={currentStep}
              style={{
                marginBottom: 32,
                padding: "0 20px",
              }}
              className="payment-setup-steps"
            >
              <Step
                title="Setup"
                description="Start onboarding"
                icon={<SettingOutlined />}
              />
              <Step
                title="Information"
                description="Provide details"
                icon={<BankOutlined />}
              />
              <Step
                title="Verification"
                description="Account review"
                icon={<SafetyOutlined />}
              />
              <Step
                title="Complete"
                description="Ready to go"
                icon={<CheckCircleOutlined />}
              />
            </Steps>

            <div
              style={{
                textAlign: "center",
                marginBottom: 32,
                padding: "0 20px",
              }}
            >
              <h2
                style={{
                  fontSize: window.innerWidth <= 768 ? "28px" : "32px",
                  marginBottom: "20px",
                  fontWeight: "700",
                  color: "#2C5F2D",
                  fontFamily:
                    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                }}
              >
                Set Up Payment Processing
              </h2>
              <p
                style={{
                  fontSize: window.innerWidth <= 768 ? "16px" : "18px",
                  color: "#4A7C59",
                  lineHeight: "1.6",
                  margin: "0 auto",
                  maxWidth: "600px",
                  fontWeight: "400",
                  fontFamily:
                    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                }}
              >
                To receive rent payments from your tenants, you need to complete
                Stripe Connect onboarding. This allows us to securely process
                payments and transfer funds to your bank account.
              </p>
            </div>

            <FeatureList>
              <FeatureItem>
                <CheckCircleOutlined />
                <span>Secure payment processing</span>
              </FeatureItem>
              <FeatureItem>
                <BankOutlined />
                <span>Direct bank transfers</span>
              </FeatureItem>
              <FeatureItem>
                <DollarOutlined />
                <span>Automatic reconciliation</span>
              </FeatureItem>
              <FeatureItem>
                <CreditCardOutlined />
                <span>PCI compliant</span>
              </FeatureItem>
            </FeatureList>

            {error && (
              <Alert
                message="Error"
                description={error}
                type="error"
                showIcon
                style={{ marginBottom: 24 }}
              />
            )}

            <div style={{ textAlign: "center" }}>
              <Button
                type="primary"
                size="large"
                loading={loading}
                onClick={startOnboarding}
                icon={loading ? <LoadingOutlined /> : <CheckCircleOutlined />}
                style={{
                  minWidth: window.innerWidth <= 768 ? "100%" : "220px",
                  height: window.innerWidth <= 768 ? "48px" : "44px",
                  fontSize: window.innerWidth <= 768 ? "16px" : "15px",
                  background:
                    "linear-gradient(135deg, #2C5F2D 0%, #4A7C59 100%)",
                  border: "none",
                  borderRadius: "16px",
                  fontWeight: "700",
                  fontFamily:
                    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                  boxShadow: "0 6px 20px rgba(44, 95, 45, 0.25)",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  position: "relative",
                  overflow: "hidden",
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = "translateY(-2px)";
                  e.target.style.boxShadow =
                    "0 8px 25px rgba(44, 95, 45, 0.35)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow =
                    "0 6px 20px rgba(44, 95, 45, 0.25)";
                }}
              >
                {loading ? "Starting Setup..." : "Start Onboarding"}
              </Button>
            </div>

            <div
              style={{
                marginTop: 32,
                fontSize: window.innerWidth <= 768 ? "13px" : "14px",
                color: "#666",
                textAlign: "center",
                padding: "0 20px",
                lineHeight: "1.5",
                fontFamily:
                  "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
              }}
            >
              <p style={{ margin: "0 0 8px 0", fontWeight: "500" }}>
                This process is secure and takes about 5 minutes to complete.
              </p>
              <p style={{ margin: "0", fontWeight: "400" }}>
                You'll be redirected to Stripe's secure platform for
                verification.
              </p>
            </div>
          </>
        );

      case "checking":
        return (
          <Result
            icon={<Spin size="large" />}
            title="Verifying Account"
            subTitle="Checking your Stripe Connect account status..."
          />
        );

      case "success":
        return (
          <SuccessContainer>
            <SuccessIcon>
              <CheckCircleOutlined />
            </SuccessIcon>

            <SuccessTitle>Payment Processing Activated</SuccessTitle>

            <SuccessSubtitle>
              Your payment processing system is now operational. Tenants can
              submit rent payments online, and funds will be automatically
              transferred to your designated bank account.
            </SuccessSubtitle>

            <CelebrationText>
              <h3>System Ready</h3>
              <p>
                Your payment processing infrastructure is now live and
                configured for rent collection.
              </p>
            </CelebrationText>

            <BenefitsGrid>
              <BenefitCard>
                <CreditCardOutlined />
                <h3>Online Payment Processing</h3>
                <p>
                  Tenants can submit rent payments 24/7 via credit card or ACH
                  bank transfer
                </p>
              </BenefitCard>

              <BenefitCard>
                <BankOutlined />
                <h3>Automated Fund Transfer</h3>
                <p>
                  Rent payments are automatically deposited into your designated
                  bank account
                </p>
              </BenefitCard>

              <BenefitCard>
                <DollarOutlined />
                <h3>Automated Reconciliation</h3>
                <p>
                  Payment records are automatically matched to tenant accounts
                  and lease agreements
                </p>
              </BenefitCard>

              <BenefitCard>
                <SafetyOutlined />
                <h3>Enterprise Security</h3>
                <p>
                  PCI DSS compliant processing with financial institution-level
                  security protocols
                </p>
              </BenefitCard>
            </BenefitsGrid>

            {accountStatus && (
              <div style={{ margin: "24px 0" }}>
                <Alert
                  message="Account Status"
                  description={
                    <div>
                      <p>
                        Payment Processing:{" "}
                        {accountStatus.charges_enabled ? "Active" : "Inactive"}
                      </p>
                      <p>
                        Fund Transfers:{" "}
                        {accountStatus.payouts_enabled ? "Active" : "Inactive"}
                      </p>
                    </div>
                  }
                  type="success"
                  showIcon
                />
              </div>
            )}

            <ActionButton type="primary" size="large" onClick={handleContinue}>
              Go To Dashboard
            </ActionButton>
          </SuccessContainer>
        );

      case "pending":
        return (
          <SuccessContainer>
            <SuccessIcon>
              <LoadingOutlined />
            </SuccessIcon>

            <SuccessTitle>Stripe Connect Account Created</SuccessTitle>

            <SuccessSubtitle>
              Your payment processing account has been successfully created! You
              must now complete Know Your Customer (KYC) verification to enable
              payment acceptance. Check your email for verification instructions
              from Stripe.
            </SuccessSubtitle>

            <CelebrationText>
              <h3>Required Actions</h3>
              <p>
                <strong>1. Check Your Email:</strong> Look for verification
                email from Stripe
              </p>
              <p>
                <strong>2. Complete KYC:</strong> Submit required documents (ID,
                bank account, business info)
              </p>
              <p>
                <strong>3. Wait for Approval:</strong> Stripe will review and
                approve within 24-48 hours
              </p>
              <p>
                <strong>4. Start Accepting Payments:</strong> Once approved, you
                can collect rent online
              </p>
            </CelebrationText>

            {accountStatus && (
              <div style={{ margin: "24px 0" }}>
                <Alert
                  message="Stripe Connect Account Status"
                  description={
                    <div>
                      <p>
                        <strong>Account ID:</strong> {accountStatus.id}
                      </p>
                      <p>
                        <strong>KYC Status:</strong>{" "}
                        {accountStatus.details_submitted
                          ? "Submitted"
                          : "Not Submitted"}
                      </p>
                      <p>
                        <strong>Payment Processing:</strong>{" "}
                        {accountStatus.charges_enabled
                          ? "✅ Active"
                          : "⏳ Pending KYC"}
                      </p>
                      <p>
                        <strong>Fund Transfers:</strong>{" "}
                        {accountStatus.payouts_enabled
                          ? "✅ Active"
                          : "⏳ Pending KYC"}
                      </p>
                    </div>
                  }
                  type="warning"
                  showIcon
                />
              </div>
            )}

            <div
              style={{ display: "flex", gap: "12px", justifyContent: "center" }}
            >
              <ActionButton
                type="primary"
                size="large"
                onClick={handleContinue}
              >
                Access Dashboard
              </ActionButton>
            </div>
          </SuccessContainer>
        );

      case "cancelled":
        return (
          <SuccessContainer>
            <ErrorIcon>
              <CloseCircleOutlined />
            </ErrorIcon>

            <ErrorTitle>Payment Setup Failed</ErrorTitle>

            <ErrorSubtitle>
              We were unable to complete your payment account setup. You may
              restart the onboarding process at any time.
            </ErrorSubtitle>

            <ErrorText>
              <h3>What Happened?</h3>
              <p>
                The payment account setup process could not be completed
                successfully. This may be due to incomplete information or a
                technical issue during the setup process.
              </p>
            </ErrorText>

            <BenefitsGrid>
              <ErrorCard>
                <SettingOutlined />
                <h3>Quick Restart</h3>
                <p>
                  You can restart the setup process immediately with all your
                  previous information saved
                </p>
              </ErrorCard>

              <ErrorCard>
                <SafetyOutlined />
                <h3>Secure Process</h3>
                <p>
                  Your account information is protected and no sensitive data
                  was stored during the failed setup
                </p>
              </ErrorCard>

              <ErrorCard>
                <BankOutlined />
                <h3>No Impact</h3>
                <p>
                  Failed setup doesn't affect your existing account or any other
                  LandlordOps features
                </p>
              </ErrorCard>

              <ErrorCard>
                <CheckCircleOutlined />
                <h3>Easy Retry</h3>
                <p>
                  Simply click "Try Again" to resume where you left off in the
                  onboarding process
                </p>
              </ErrorCard>
            </BenefitsGrid>

            <div style={{ margin: "24px 0" }}>
              <Alert
                message="Setup Status"
                description="Payment processing setup failed. You'll need to complete onboarding to accept rent payments from tenants."
                type="warning"
                showIcon
              />
            </div>

            <div
              style={{
                display: "flex",
                gap: "16px",
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <ActionButton type="primary" size="large" onClick={handleRetry}>
                Try Again
              </ActionButton>
              <SecondaryButton size="large" onClick={handleContinue}>
                Go To Dashboard
              </SecondaryButton>
            </div>
          </SuccessContainer>
        );

      case "error":
      default:
        return (
          <SuccessContainer>
            <ErrorIcon>
              <CloseCircleOutlined />
            </ErrorIcon>

            <ErrorTitle>Setup Process Error</ErrorTitle>

            <ErrorSubtitle>
              {error ||
                "An error occurred during the payment processing configuration."}
            </ErrorSubtitle>

            <ErrorText>
              <h3>What Went Wrong?</h3>
              <p>
                There was a technical issue during the payment setup process.
                This could be due to network connectivity, server maintenance,
                or a temporary service interruption.
              </p>
            </ErrorText>

            <BenefitsGrid>
              <ErrorCard>
                <SettingOutlined />
                <h3>Retry Setup</h3>
                <p>
                  Most issues are temporary and can be resolved by restarting
                  the setup process
                </p>
              </ErrorCard>

              <ErrorCard>
                <SafetyOutlined />
                <h3>Secure System</h3>
                <p>
                  Your account remains secure and no sensitive information was
                  compromised
                </p>
              </ErrorCard>

              <ErrorCard>
                <BankOutlined />
                <h3>No Data Loss</h3>
                <p>
                  Any information you entered during setup is safely stored and
                  will be available when you retry
                </p>
              </ErrorCard>

              <ErrorCard>
                <CheckCircleOutlined />
                <h3>Support Available</h3>
                <p>
                  If issues persist, our support team can help troubleshoot the
                  problem
                </p>
              </ErrorCard>
            </BenefitsGrid>

            <div style={{ margin: "24px 0" }}>
              <Alert
                message="Error Details"
                description={
                  error ||
                  "A technical error occurred during payment processing setup. Please try again or contact support if the issue persists."
                }
                type="error"
                showIcon
              />
            </div>

            <div
              style={{
                display: "flex",
                gap: "16px",
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <ActionButton type="primary" size="large" onClick={handleRetry}>
                Try Again
              </ActionButton>
              <SecondaryButton size="large" onClick={handleContinue}>
                Go To Dashboard
              </SecondaryButton>
            </div>
          </SuccessContainer>
        );
    }
  };

  return (
    <Container>
      {showConfetti && <ConfettiBurst onComplete={handleConfettiComplete} />}
      <SetupCard title={mode === "setup" ? "Payment Processing Setup" : null}>
        {renderContent()}
      </SetupCard>
    </Container>
  );
};

export default PaymentSetup;
